// Phase E1, meta and onboarding (gate E1, decision 0060), on the page over the joined valley's meta layer
// (./module.ts, a kernel module from ./scene.ts):
//   - achievements and the codex: the screens (./screens.ts) from the pause screen, an unlock notice (and one for a new
//     codex entry) written straight to the DOM, the profile kept beside the settings in the save store and synced with
//     a merge (./profile.ts), loaded at start and whenever a sync brings it in;
//   - tutorial prompts (./tutorials.ts), with the `meta.hints` setting;
//   - photo mode (./photo.ts) from the pause screen.
// Handle keys under `__rpg.meta`; planted defects `tutorial-never-retires` and `photo-unbounded` (@rpg/meta's META_FAULTS).
import type * as THREE from "three";
import { META_FAULTS } from "@rpg/meta";
import { directText, type ScreenRender, type Settings, type Ui } from "@rpg/ui";
import type { DeviceFamily, Input, Runtime } from "@rpg/runtime";
import type { EventBus } from "@rpg/kernel";
import type { GameEvents } from "../core";
import type { SpikeContent } from "../content";
import type { FaultGroup } from "../faults";
import type { FlowHandle } from "../boot";
import type { SpikeSaves } from "../save";
import type { PhotoHooks } from "../sim";
import type { MetaLayer } from "./module";
import { applyProfile, mergeProfileBytes, META_PROFILE_KEY, profileOf } from "./profile";
import { metaScreens } from "./screens";
import { mountTutorials, type TutorialView } from "./tutorials";
import { mountPhoto, type PhotoView } from "./photo";
import { mountLorePages } from "./pages";
import type { PlayState } from "./facts";
import type { Conditions } from "@rpg/story";

/** The meta layer's planted defects, as `?faults=` and `__rpg.faults()` name them. */
export const META_FAULT_NAMES = ["tutorial-never-retires", "photo-unbounded"] as const;
const SWITCH: Record<(typeof META_FAULT_NAMES)[number], keyof typeof META_FAULTS> = { "tutorial-never-retires": "tutorialNeverRetires", "photo-unbounded": "photoUnbounded" };

/** The group for src/faults.ts's joined record; reads `?faults=` for its own names when made. */
export function metaFaultGroup(search = typeof location !== "undefined" ? location.search : ""): FaultGroup {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const n of META_FAULT_NAMES) if (on.includes(n)) META_FAULTS[SWITCH[n]] = true;
  return {
    names: META_FAULT_NAMES,
    get: (n) => META_FAULTS[SWITCH[n as (typeof META_FAULT_NAMES)[number]]] === true,
    set: (n, v) => void (META_FAULTS[SWITCH[n as (typeof META_FAULT_NAMES)[number]]] = v),
  };
}

const CSS = `
.gv-meta-notice{position:fixed;right:max(16px,env(safe-area-inset-right));top:34%;z-index:13;display:none;align-items:center;gap:10px;max-width:min(44vw,340px);
  padding:10px 14px;border-radius:12px;background:rgba(29,42,31,.9);color:#fbf6e8;font:500 calc(14px * var(--rpg-text-scale,1))/1.3 system-ui,sans-serif;
  box-shadow:0 4px 16px rgba(0,0,0,.28);border:1px solid rgba(233,196,106,.6);pointer-events:none}
.gv-meta-notice[data-shown]{display:flex}
.gv-meta-notice svg{width:2.2em;height:2.2em;flex:none}
.gv-meta-notice b{display:block;font-weight:700;color:#e9c46a;font-size:.85em;letter-spacing:.04em;text-transform:uppercase}
`;

/** What the meta layer's page needs. */
export interface MetaDeps {
  runtime: Runtime;
  content: SpikeContent;
  layer: MetaLayer;
  events: EventBus<GameEvents>;
  ui: Ui;
  addScreens(extra: Record<string, ScreenRender>, pause?: { label: string; action: string; screen: string }[]): void;
  settings: Settings;
  saves: SpikeSaves;
  flow: FlowHandle;
  input: Input;
  glyphs: Readonly<Partial<Record<DeviceFamily, Readonly<Record<string, string>>>>>;
  conditions: Conditions | null;
  /** The rpg layer's glyph count. */
  glyphCount(): number;
  play(): PlayState;
  /** A cutscene playing (no prompt over it). */
  cutscene(): boolean;
  /** The dev harness's compile watchdog excuse (deliberate work: photo mode), when there is one. */
  excuse?: (reason: string) => void;
  /** Photo mode's view parts (null: no photo mode on this scene). */
  photo: { hooks: PhotoHooks; scene: THREE.Scene; camera: THREE.PerspectiveCamera; renderer: THREE.WebGLRenderer; tier: string } | null;
  /** The dev editor holds the camera's overlay layer: photo mode waits (dev builds; always false in production). */
  photoBlocked?: () => boolean;
}

/** Mount the meta layer's page parts. */
export function mountMeta(d: MetaDeps) {
  if (!document.getElementById("gv-meta-notice-css")) {
    const s = document.createElement("style");
    s.id = "gv-meta-notice-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  const t = d.content.t;
  const layer = d.layer;

  // --- the screens -----------------------------------------------------------------------------------------------
  const sc = metaScreens({ content: d.content, layer: () => layer, glyphs: d.glyphCount });
  let photo: PhotoView | null = null;
  if (d.photo) photo = mountPhoto({ runtime: d.runtime, content: d.content, meta: layer.content, hooks: d.photo.hooks, ui: d.ui, scene: d.photo.scene, camera: d.photo.camera, renderer: d.photo.renderer, tier: d.photo.tier, ...(d.excuse ? { excuse: d.excuse } : {}), ...(d.photoBlocked ? { blocked: d.photoBlocked } : {}), speed: () => Number(d.settings.get("photo.speed") ?? 1) });
  d.addScreens({ ...sc.screens, ...(photo ? photo.screens : {}) }, [...sc.pause, ...(photo ? photo.pause : [])]);
  // the lore pages lying in the world (drawn on the valley's look; gone once found)
  const look = d.photo?.hooks.look() ?? null;
  const pages = d.photo && look ? mountLorePages({ runtime: d.runtime, scene: d.photo.scene, look, layer, ground: d.photo.hooks.ground }) : null;

  // --- the profile: loaded, merged, written -----------------------------------------------------------------------
  let tips: TutorialView | null = null;
  let loaded = false;
  let writes = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const write = (): Promise<void> => {
    if (timer) clearTimeout(timer);
    timer = null;
    writes++;
    return d.saves.saveValue(META_PROFILE_KEY, profileOf(layer, tips?.tutorials ?? null)).catch((e) => console.warn("[meta] profile not saved", e));
  };
  const soon = (): void => {
    if (!loaded) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void write(), 300);
  };
  d.saves.setMerge(META_PROFILE_KEY, mergeProfileBytes);
  const load = (): Promise<void> =>
    d.saves
      .loadValue(META_PROFILE_KEY)
      .then((raw) => {
        if (raw) applyProfile(layer, tips?.tutorials ?? null, raw);
        layer.check();
      })
      .catch((e) => console.warn("[meta] profile not loaded", e))
      .finally(() => {
        const first = !loaded;
        loaded = true;
        // what this page did before the load joins what was stored
        if (first) void write();
      });
  const ready = load();
  d.saves.onPulled((key) => key === META_PROFILE_KEY && void load());
  // the flow's pause and quit write it too (the codex's counts move without a find)
  d.flow.on((e) => (e.to === "pause" || e.to === "title") && soon());

  // --- the notices ------------------------------------------------------------------------------------------------
  const notice = document.createElement("div");
  notice.className = "gv-meta-notice";
  notice.setAttribute("role", "status");
  notice.dataset.metaNotice = "";
  const icon = document.createElement("span");
  icon.setAttribute("aria-hidden", "true");
  const text = document.createElement("span");
  const head = document.createElement("b");
  const body = document.createElement("span");
  text.append(head, body);
  notice.append(icon, text);
  document.body.appendChild(notice);
  const headText = directText(head);
  const bodyText = directText(body);
  const queue: { kind: "achievement" | "codex"; id: string }[] = [];
  const shownLog: { kind: string; id: string }[] = [];
  let showing: { kind: string; id: string; left: number } | null = null;
  const NOTICE_S = 4;
  const badgeSvg = (iconId: string): string => {
    // the list's badge drawn small (the same paths, gold)
    const paths: Record<string, string> = { blade: "M5 19 17 5l2 2-12 12z", crown: "M4 17 3 7l5 4 4-6 4 6 5-4-1 10z", leaf: "M4 20C4 10 11 4 20 4c0 9-6 16-16 16z", glyph: "M12 2 21 12 12 22 3 12z", book: "M4 5c3-1 6-1 8 1v14c-2-2-5-2-8-1z", compass: "M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18z", star: "M12 3l2.6 5.6 6 .7-4.5 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6L3.4 9.3l6-.7z", hand: "M7 21v-8l-2-3 1-1 3 3V5a1.5 1.5 0 0 1 3 0v6-8a1.5 1.5 0 0 1 3 0v8-6a1.5 1.5 0 0 1 3 0v10c0 4-2 6-5 6z", scroll: "M6 4h11a2 2 0 0 1 0 4H8v12a2 2 0 0 1-4 0V6a2 2 0 0 1 2-2z" };
    return `<svg viewBox="0 0 24 24" focusable="false"><path d="${paths[iconId] ?? paths.star}" fill="#e9c46a" stroke="#1d2a1f" stroke-width="1.2" stroke-linejoin="round"/></svg>`;
  };
  const achRows = new Map(layer.content.achievementRows.map((r) => [r.id, r]));
  const codexRows = new Map(layer.content.codexRows.map((r) => [r.id, r]));
  const present = (n: { kind: "achievement" | "codex"; id: string }): void => {
    if (n.kind === "achievement") {
      const r = achRows.get(n.id)!;
      icon.innerHTML = badgeSvg(r.icon);
      headText.set(t("ach.notice"));
      bodyText.set(t(r.name.id));
    } else {
      const r = codexRows.get(n.id)!;
      icon.innerHTML = badgeSvg(r.section === "bestiary" ? "book" : "scroll");
      headText.set(t("codex.notice"));
      bodyText.set(t(r.name.id));
    }
    notice.dataset.kind = n.kind;
    notice.dataset.id = n.id;
    notice.dataset.shown = "";
    showing = { ...n, left: NOTICE_S };
    shownLog.push({ kind: n.kind, id: n.id });
    if (shownLog.length > 64) shownLog.splice(0, 32);
  };
  d.events.on("meta:unlocked", (e) => {
    queue.push({ kind: "achievement", id: e.id });
    soon();
  });
  d.events.on("meta:found", (e) => {
    queue.push({ kind: "codex", id: e.entry });
    soon();
  });
  const visible = (): boolean => d.flow.state() === "play" && d.ui.top() === null && !d.cutscene();
  d.runtime.on(
    "late",
    (c) => {
      if (showing) {
        // a notice's time runs while it can be seen
        if (visible()) showing.left -= Math.max(0, c.realDt > 0 ? c.realDt : c.dt);
        if (showing.left <= 0) {
          showing = null;
          notice.removeAttribute("data-shown");
        }
      }
      if (!showing && queue.length && visible()) present(queue.shift()!);
      if (showing && !visible()) notice.removeAttribute("data-shown");
      else if (showing) notice.dataset.shown = "";
    },
    { order: 61, name: "meta: notices" },
  );

  // --- the tutorials ----------------------------------------------------------------------------------------------
  tips = mountTutorials({
    runtime: d.runtime,
    content: d.content,
    meta: layer.content,
    input: d.input,
    glyphs: d.glyphs,
    conditions: d.conditions,
    play: d.play,
    showing: visible,
    hints: () => d.settings.get("meta.hints") !== false,
    mapOpen: () => d.ui.top() === "map",
    onRetire: () => soon(),
  });

  const handle = {
    /** Every achievement's progress, in the table's order. */
    achievements: () => layer.achievements.list(),
    unlocked: () => layer.achievements.list().filter((a) => a.unlocked).map((a) => a.id),
    /** A codex section's entries (bestiary, lore), or the glyph collection. */
    codex: (section: string) => layer.codex.section(section),
    glyphs: () => ({ count: d.glyphCount(), max: layer.content.maxGlyphs }),
    /** The records fed lately, and the lore pages. */
    fed: () => layer.fed(),
    pages: () => layer.pages(),
    /** The lore pages drawn now (not yet found). */
    pagesShown: () => pages?.shown() ?? [],
    /** The notice: what shows now, what has shown, what waits. */
    notice: () => ({ shown: notice.hasAttribute("data-shown"), kind: notice.dataset.kind ?? null, id: notice.dataset.id ?? null, text: notice.hasAttribute("data-shown") ? notice.textContent : null, log: shownLog.map((x) => ({ ...x })), queued: queue.length }),
    /** The profile: as it would be written now; write it now; load and merge the stored one; how many writes. */
    profile: () => JSON.parse(JSON.stringify(profileOf(layer, tips?.tutorials ?? null))),
    writeProfile: () => write().then(() => true),
    loadProfile: () => load().then(() => true),
    ready: () => ready.then(() => true),
    writes: () => writes,
    /** The tutorial card, the prompts' retirement, the uses heard; reset every prompt. */
    tip: () => tips!.state(),
    tutorials: () => tips!.tutorials.list(),
    tutorialUses: () => tips!.used(),
    resetTutorials: () => {
      tips!.tutorials.reset();
      soon();
    },
    /** Photo mode (null on a scene without it). */
    photo: photo
      ? {
          open: () => photo!.open(),
          close: () => d.ui.back(),
          state: () => photo!.report(),
          move: photo.move,
          orbit: photo.orbit,
          zoom: photo.zoom,
          setHours: photo.setHours,
          setPose: photo.setPose,
          setDof: photo.setDof,
          hidePanel: photo.hidePanel,
          capture: () => photo!.capture().then(() => photo!.report().lastCapture),
          joint: (id: string) => photo!.joint(id),
        }
      : null,
  };
  return {
    handle,
    tips,
    photo,
    /** The camera consumed a look delta (the look tutorial's verb). */
    noteLook: (): void => tips?.noteLook(),
    /** Photo mode is up (main.ts keeps drawing frames under it while the game is paused). */
    photoActive: (): boolean => photo?.active() ?? false,
  };
}
